import React from "react";
import ServiceHeroSection from "../common/service-hero-section";
import { AiBlog } from "@/components/main";
import {
  FeatureGrid,
  ProcessSteps,
  RelatedServices,
  ScenarioGrid,
  ServiceAudience,
  ServiceBottomCta,
  ServiceFaq,
  ServiceSection,
  ServiceSectionHeader,
  SplitContent,
} from "../common/service-content-blocks";

const APPLICATION_TYPES = [
  {
    title: "Knowledge assistants",
    description:
      "Grounded Q&A over policies, manuals, and internal docs with citations your teams can verify.",
  },
  {
    title: "Document intelligence",
    description:
      "Extract, classify, and structure content from contracts, invoices, and operational records.",
  },
  {
    title: "Decision support",
    description:
      "Summaries, comparisons, and recommendations that keep humans in the decision loop.",
  },
  {
    title: "Domain copilots",
    description:
      "Role-specific assistants for ops, support, finance, or compliance with controlled tool access.",
  },
  {
    title: "Retrieval platforms",
    description:
      "Shared RAG infrastructure with evaluation, access control, and content freshness workflows.",
  },
  {
    title: "Evaluation harnesses",
    description:
      "Test sets, regression checks, and quality gates so models stay reliable after launch.",
  },
];

const PRODUCTION = [
  {
    title: "Retrieval and grounding",
    description:
      "Approved sources, chunking strategy, and citation requirements so answers stay verifiable.",
  },
  {
    title: "Access control",
    description:
      "Respect identity and document permissions already defined in your environment.",
  },
  {
    title: "Evaluation loops",
    description:
      "Golden sets, regression checks, and operator feedback before and after go-live.",
  },
  {
    title: "Cost and latency budgets",
    description:
      "Model and retrieval choices tied to real traffic patterns, not demo settings.",
  },
];

const SCENARIOS = [
  {
    title: "Policy and SOP assistants",
    description:
      "Employees get grounded answers with citations instead of pinging specialists for the same questions.",
    outcome: "Faster answers, fewer repeated interruptions.",
  },
  {
    title: "Contract and claims intake",
    description:
      "Extract fields, flag missing data, and route packages into the system of record.",
    outcome: "Cleaner intake with human review on exceptions.",
  },
  {
    title: "Support knowledge copilots",
    description:
      "Agents and support staff draft replies from approved knowledge with clear refusal when sources are thin.",
    outcome: "More consistent responses with less tribal knowledge.",
  },
  {
    title: "Ops decision briefs",
    description:
      "Summarize multi-source status into a short brief an operator can act on.",
    outcome: "Less time assembling context, more time deciding.",
  },
];

const DELIVERY = [
  {
    title: "Define the job",
    description: "Clarify users, questions, data sources, and success metrics.",
  },
  {
    title: "Shape the stack",
    description: "Choose models, retrieval, and governance for your constraints.",
  },
  {
    title: "Build and measure",
    description: "Ship with evaluation against real queries and operator feedback.",
  },
  {
    title: "Operate and improve",
    description: "Monitor quality, refresh content, and tighten failure modes.",
  },
];

export const FAQS = [
  {
    question: "Do you fine-tune models or use retrieval?",
    answer:
      "We choose based on the problem. Many enterprise use cases perform best with strong retrieval, evaluation, and prompt design. Fine-tuning is used when it clearly improves quality or cost.",
  },
  {
    question: "Can this run in our cloud or VPC?",
    answer:
      "Yes. We design for your hosting, identity, and data residency requirements.",
  },
  {
    question: "How do you reduce hallucinations?",
    answer:
      "Grounding in approved sources, citation requirements, refusal behavior, and continuous evaluation against known-good answers.",
  },
  {
    question: "Who maintains content after launch?",
    answer:
      "Your content owners. We set up freshness workflows and evaluation so updates do not silently degrade quality.",
  },
];

function CustomLlmDevelopmentComponent({ posts = [] }) {
  return (
    <>
      <ServiceHeroSection
        headlinePrimary="LLM applications"
        headlineHighWhite="for enterprise work"
        descriptionPrimary="We build language model applications that retrieve from your knowledge, respect access controls, and fit the way your teams already work."
        descriptionSecondary="Assistants, document systems, and decision support designed for production, not one-off experiments."
      />

      <ServiceAudience
        forItems={[
          "You have approved knowledge sources teams already trust",
          "Users need grounded answers or structured extraction",
          "Access control and audit matter",
          "You can define success with real sample queries",
        ]}
        notForItems={[
          "You want a generic public chatbot with no grounding",
          "There is no content owner for source material",
          "You need creative generation without verification",
          "Hosting and identity constraints are unknown",
        ]}
      />

      <ServiceSection>
        <ServiceSectionHeader
          title="What we build with LLMs"
          lead="Application patterns chosen for operational fit, not novelty."
        />
        <FeatureGrid items={APPLICATION_TYPES} />
      </ServiceSection>

      <ServiceSection muted>
        <SplitContent
          title="Production requirements we design for"
          lead="Quality holds after launch only if retrieval, access, evaluation, and cost are treated as product requirements."
          points={PRODUCTION}
        />
      </ServiceSection>

      <ServiceSection>
        <ServiceSectionHeader
          title="Where LLM apps create value"
          lead="Concrete starting points with outcomes your operators will recognize."
        />
        <ScenarioGrid items={SCENARIOS} />
      </ServiceSection>

      <ServiceSection muted>
        <ServiceSectionHeader
          title="How we deliver"
          lead="From job definition to a system you can evaluate and operate."
        />
        <ProcessSteps steps={DELIVERY} />
      </ServiceSection>

      <ServiceFaq items={FAQS} />
      <ServiceBottomCta
        title="Ready to scope an LLM application?"
        description="Share the users, sources, and questions that matter. We will map a practical architecture and pilot plan."
      />
      <RelatedServices
        items={[
          { href: "/ai-agent-development", label: "AI agents" },
          { href: "/ai-workflow-automation", label: "Workflow automation" },
          { href: "/ai-integrations", label: "Integrations" },
          { href: "/mvp-poc-development", label: "MVP and PoC" },
        ]}
      />
      <AiBlog posts={posts} />
    </>
  );
}

export default CustomLlmDevelopmentComponent;
